import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiBaseQuery, Methods } from '@app/api/shared';
import {
  type UnarchiveUserRequest,
  UserEndpoints as Endpoints,
  type AddEmployeePortfolioRequest,
  type AddEmployeePortfolioResponse,
  type ChangeUserPhoneNumberRequest,
  type ChangeUserPhoneNumberResponse,
  type CreateSecretLoginRequest,
  type CreateSecretLoginResponse,
  type DeleteEmployeePortfolioRequest,
  type DeleteEmployeePortfolioResponse,
  type GetUserNamesRequest,
  type GetUserNamesResponse,
  type GetUserByIdRequest,
  type GetUserByIdResponse,
  type GetUserSettingsRequest,
  type GetUserSettingsResponse,
  type GetUserProfileForEditRequest,
  type GetUserProfileForEditResponse,
  type GetUserProfileForViewRequest,
  type GetUserProfileForViewResponse,
  type NormalizeUserPhotoRequest,
  type NormalizeUserPhotoResponse,
  type PrepareEmailChangeRequest,
  type PrepareEmailChangeResponse,
  type GetEmployeePortfolioRequest,
  type GetEmployeePortfolioResponse,
  type SendoutInviteLetterRequest,
  type SendoutInviteLetterResponse,
  type SetUserActiveBranchRequest,
  type SetUserActiveBranchResponse,
  type SetUserPhotoRequest,
  type SetUserPhotoResponse,
  type UpdateEmployeePortfolioRequest,
  type UpdateEmployeePortfolioResponse,
  type UpdateUserRequest,
  type UpdateUserResponse,
  type UpdateEmailRequest,
  type UpdateEmailResponse,
  type UpdateUserPasswordRequest,
  type UpdateUserPasswordResponse,
  type UpdateUserProfileRequest,
  type UpdateUserProfileResponse,
  type UpdateUserSettingsRequest,
  type UpdateUserSettingsResponse,
  type VerifyEmailChangeRequest,
  type VerifyEmailChangeResponse,
} from '@app/api/User/types';
import { USER_RESOURCE } from '@app/constants';

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: createApiBaseQuery(USER_RESOURCE),
  endpoints: ({ mutation, query }) => ({
    [Endpoints.CreateSecretLogin]: query<
      CreateSecretLoginResponse,
      CreateSecretLoginRequest
    >({
      query: (params) => ({
        url: Endpoints.CreateSecretLogin,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetEmployeePortfolio]: query<
      GetEmployeePortfolioResponse,
      GetEmployeePortfolioRequest
    >({
      query: (params) => ({
        url: Endpoints.GetEmployeePortfolio,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.AddEmployeePortfolio]: mutation<
      AddEmployeePortfolioResponse,
      AddEmployeePortfolioRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.AddEmployeePortfolio,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.DeleteEmployeePortfolio]: mutation<
      DeleteEmployeePortfolioResponse,
      DeleteEmployeePortfolioRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.DeleteEmployeePortfolio,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.UpdateEmployeePortfolio]: mutation<
      UpdateEmployeePortfolioResponse,
      UpdateEmployeePortfolioRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateEmployeePortfolio,
        method: Methods.POST,
        params,
        data,
      }),
    }),

    [Endpoints.GetUserById]: query<GetUserByIdResponse, GetUserByIdRequest>({
      query: (params) => ({
        url: Endpoints.GetUserById,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SetUserActiveBranch]: mutation<
      SetUserActiveBranchResponse,
      SetUserActiveBranchRequest
    >({
      query: (params) => ({
        url: Endpoints.SetUserActiveBranch,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.GetUserProfileForView]: query<
      GetUserProfileForViewResponse,
      GetUserProfileForViewRequest
    >({
      query: (params) => ({
        url: Endpoints.GetUserProfileForView,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetUserProfileForEdit]: query<
      GetUserProfileForEditResponse,
      GetUserProfileForEditRequest
    >({
      query: ({ logs, ...params }) => ({
        url: Endpoints.GetUserProfileForEdit,
        method: Methods.GET,
        params,
        logs,
      }),
    }),

    [Endpoints.GetUserSettings]: query<
      GetUserSettingsResponse,
      GetUserSettingsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetUserSettings,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.UpdateUserSettings]: mutation<
      UpdateUserSettingsResponse,
      UpdateUserSettingsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateUserSettings,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.PrepareEmailChange]: mutation<
      PrepareEmailChangeResponse,
      PrepareEmailChangeRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.PrepareEmailChange,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.VerifyEmailChange]: mutation<
      VerifyEmailChangeResponse,
      VerifyEmailChangeRequest
    >({
      query: (params) => ({
        url: Endpoints.VerifyEmailChange,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.UpdateUserPassword]: mutation<
      UpdateUserPasswordResponse,
      UpdateUserPasswordRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateUserPassword,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.UpdateUserProfile]: mutation<
      UpdateUserProfileResponse,
      UpdateUserProfileRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateUserProfile,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.SetUserPhoto]: mutation<
      SetUserPhotoResponse,
      SetUserPhotoRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SetUserPhoto,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.NormalizeUserPhoto]: mutation<
      NormalizeUserPhotoResponse,
      NormalizeUserPhotoRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.NormalizeUserPhoto,
        method: Methods.POST,
        params,
        data,
      }),
    }),

    [Endpoints.UpdateUser]: mutation<UpdateUserResponse, UpdateUserRequest>({
      query: ({ data, logs, ...params }) => ({
        url: Endpoints.UpdateUser,
        method: Methods.POST,
        params,
        data,
        logs,
      }),
    }),
    [Endpoints.UpdateEmail]: mutation<UpdateEmailResponse, UpdateEmailRequest>({
      query: (params) => ({
        url: Endpoints.UpdateEmail,
        method: Methods.PUT,
        params,
      }),
    }),
    [Endpoints.GetUserNames]: query<GetUserNamesResponse, GetUserNamesRequest>({
      query: (params) => ({
        url: Endpoints.GetUserNames,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SendoutInviteLetter]: mutation<
      SendoutInviteLetterResponse,
      SendoutInviteLetterRequest
    >({
      query: (params) => ({
        url: Endpoints.SendoutInviteLetter,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.ChangeUserPhoneNumber]: mutation<
      ChangeUserPhoneNumberResponse,
      ChangeUserPhoneNumberRequest
    >({
      query: (params) => ({
        url: Endpoints.ChangeUserPhoneNumber,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.UnarchiveUser]: mutation<void, UnarchiveUserRequest>({
      query: (params) => ({
        url: Endpoints.UnarchiveUser,
        method: Methods.POST,
        params,
      }),
    }),
  }),
});

export const {
  useCreateSecretLoginQuery,
  useLazyCreateSecretLoginQuery,
  useGetEmployeePortfolioQuery,
  useLazyGetEmployeePortfolioQuery,
  useAddEmployeePortfolioMutation,
  useDeleteEmployeePortfolioMutation,
  useUpdateEmployeePortfolioMutation,
  useGetUserByIdQuery,
  useLazyGetUserByIdQuery,
  useSetUserActiveBranchMutation,
  useGetUserProfileForViewQuery,
  useLazyGetUserProfileForViewQuery,
  useGetUserProfileForEditQuery,
  useLazyGetUserProfileForEditQuery,
  useGetUserSettingsQuery,
  useLazyGetUserSettingsQuery,
  useUpdateUserSettingsMutation,
  usePrepareEmailChangeMutation,
  useVerifyEmailChangeMutation,
  useUpdateUserPasswordMutation,
  useUpdateUserProfileMutation,
  useSetUserPhotoMutation,
  useNormalizeUserPhotoMutation,
  useUpdateUserMutation,
  useUpdateEmailMutation,
  useGetUserNamesQuery,
  useLazyGetUserNamesQuery,
  useSendoutInviteLetterMutation,
  useChangeUserPhoneNumberMutation,
  useUnarchiveUserMutation,
} = userApi;
